import {
    type DeepPartial,
    FRUITFUL_INSTITUTION_NAME,
    getApexRegistrationOwnershipLabel,
    getLegacyImAccountOwnershipLabel,
    type MoneyMapAccount,
    type MoneyMapAccountCreateParams,
    MoneyMapAccountStatus,
    MoneyMapAccountType,
    type MoneyMapAccountWithAccountDetails,
    type MoneyMapApexAccountCreateParameters,
    type MoneyMapApexAccountDetails,
    type MoneyMapBankAccountDetails,
    type MoneyMapCashAccountCreateParameters,
    type MoneyMapIMAccountCreateParameters,
    type MoneyMapIMAccountDetails,
    MoneyMapRuleType,
    type MoneyMapRuleWithAccount,
    type MoneyMapWithRulesAndAccounts,
    RelationshipStatus,
} from '@fruitful/types';
import { orderBy } from 'lodash';

/**
 * Synthesizes `accountDetails` from `createParameters` for a proposed account.
 * Returns undefined if the account type is not a Fruitful-managed type.
 */
const synthesizeAccountDetails = (
    type: MoneyMapAccountType | undefined,
    createParameters: DeepPartial<MoneyMapAccountCreateParams> | undefined,
):
    | DeepPartial<
          | MoneyMapBankAccountDetails
          | MoneyMapIMAccountDetails
          | MoneyMapApexAccountDetails
      >
    | undefined => {
    if (createParameters == null) {
        return undefined;
    }
    if (type === MoneyMapAccountType.INTERNAL_BANKING) {
        const cp = createParameters as MoneyMapCashAccountCreateParameters;
        return {
            ...cp,
            institutionName: FRUITFUL_INSTITUTION_NAME,
            hasVirtualCard: cp.virtualCard,
            hasPhysicalCard: cp.physicalCard,
        } satisfies DeepPartial<MoneyMapBankAccountDetails>;
    }
    if (type === MoneyMapAccountType.INVESTMENT_MANAGEMENT) {
        return {
            ...(createParameters as MoneyMapIMAccountCreateParameters),
            institutionName: FRUITFUL_INSTITUTION_NAME,
        } satisfies DeepPartial<MoneyMapIMAccountDetails>;
    }
    if (type === MoneyMapAccountType.APEX_INVESTMENT_MANAGEMENT) {
        return {
            ...(createParameters as MoneyMapApexAccountCreateParameters),
            institutionName: FRUITFUL_INSTITUTION_NAME,
        } satisfies DeepPartial<MoneyMapApexAccountDetails>;
    }
    return undefined;
};

/**
 * Merges `synthesized` (from createParameters) with `existing` accountDetails.
 * Existing non-undefined values take precedence so backend-set values (including explicit nulls) win.
 */
const mergeAccountDetails = <T extends Record<string, unknown>>(
    synthesized: T,
    existing: Record<string, unknown> | undefined,
): T => {
    if (existing == null) {
        return synthesized;
    }
    const merged = { ...synthesized };
    for (const [key, value] of Object.entries(existing)) {
        if (value !== undefined) {
            (merged as Record<string, unknown>)[key] = value;
        }
    }
    return merged;
};

export const getAccountWithDetails = (
    accounts: Record<string, MoneyMapAccountWithAccountDetails>,
    moneyMapAccountId?: string,
    moneyMapAccount?: DeepPartial<MoneyMapAccount>,
): DeepPartial<MoneyMapAccountWithAccountDetails> | undefined => {
    let account: DeepPartial<MoneyMapAccountWithAccountDetails> | undefined =
        accounts[moneyMapAccountId ?? (moneyMapAccount?.id as string)];

    // Handle unsaved proposed accounts (not yet in the accounts map)
    const isUnsavedProposedAccount =
        account == null &&
        moneyMapAccount?.status === MoneyMapAccountStatus.PROPOSED &&
        moneyMapAccount?.createParameters != null;

    if (isUnsavedProposedAccount) {
        const synthesized = synthesizeAccountDetails(
            moneyMapAccount?.type,
            moneyMapAccount?.createParameters,
        );
        if (synthesized != null) {
            account = {
                ...moneyMapAccount,
                accountDetails: synthesized,
            };
        }
    }

    // Fill incomplete accountDetails for saved proposed accounts from createParameters
    if (
        account?.status === MoneyMapAccountStatus.PROPOSED &&
        account.createParameters != null
    ) {
        const synthesized = synthesizeAccountDetails(
            account.type,
            account.createParameters,
        );
        if (synthesized != null) {
            account = {
                ...account,
                accountDetails: mergeAccountDetails(
                    synthesized,
                    account.accountDetails as
                        | Record<string, unknown>
                        | undefined,
                ),
            };
        }
    }

    return account;
};

export const getAccountWithDetailsForRule = (
    rule: DeepPartial<MoneyMapRuleWithAccount>,
    accounts: Record<string, MoneyMapAccountWithAccountDetails>,
    moneyMap?: DeepPartial<MoneyMapWithRulesAndAccounts>,
): DeepPartial<MoneyMapAccountWithAccountDetails> | undefined => {
    const ruleAccountId = rule.moneyMapAccountId ?? rule.moneyMapAccount?.id;

    const incomeAccountId =
        moneyMap?.incomeMoneyMapAccountId ??
        moneyMap?.incomeMoneyMapAccount?.id;

    const nestedAccount =
        moneyMap != null &&
        incomeAccountId != null &&
        ruleAccountId != null &&
        incomeAccountId === ruleAccountId
            ? (moneyMap.incomeMoneyMapAccount ?? rule.moneyMapAccount)
            : rule.moneyMapAccount;

    return getAccountWithDetails(
        accounts,
        rule.moneyMapAccountId,
        nestedAccount,
    );
};

/**
 * Destination is the rule account (often an external). When it has a Fruitful
 * transfer account, that Fruitful account is primary — same as the old map row.
 */
export const getDisplayAccountsForMoneyMapRule = (
    rule: {
        moneyMapAccountId?: string | null;
        moneyMapAccount?: MoneyMapAccountWithAccountDetails;
    },
    accountsById: Record<string, MoneyMapAccountWithAccountDetails>,
): {
    destinationAccount?: MoneyMapAccountWithAccountDetails;
    primaryAccount?: MoneyMapAccountWithAccountDetails;
    secondaryAccount?: MoneyMapAccountWithAccountDetails;
} => {
    const destinationAccount =
        (rule.moneyMapAccountId != null
            ? accountsById[rule.moneyMapAccountId]
            : undefined) ?? rule.moneyMapAccount;

    if (destinationAccount == null) {
        return {};
    }

    const linkedBackdoorAccount =
        destinationAccount.backdoorRothLinkedMoneyMapAccount ??
        (destinationAccount.backdoorRothLinkedMoneyMapAccountId != null
            ? accountsById[
                  destinationAccount.backdoorRothLinkedMoneyMapAccountId
              ]
            : undefined);

    if (linkedBackdoorAccount != null) {
        return {
            destinationAccount,
            primaryAccount: destinationAccount,
            secondaryAccount: linkedBackdoorAccount,
        };
    }

    const transferAccountId = destinationAccount.moneyMapAccountIdForTransfer;
    const transferAccount =
        transferAccountId != null ? accountsById[transferAccountId] : undefined;

    return {
        destinationAccount,
        primaryAccount: transferAccount ?? destinationAccount,
        secondaryAccount:
            transferAccount != null ? destinationAccount : undefined,
    };
};

/**
 * Whether `userId` is an owner of this account — either the primary account
 * holder, or a co-owner on a joint account. The money map's account list is
 * already scoped to the member's own membership (which may include a
 * partner), so this decides whether a *specific* household member should be
 * able to pick the account as a new goal's destination — not whether the
 * account belongs to the membership at all.
 */
export const isMoneyMapAccountOwnedByUser = (
    account: MoneyMapAccountWithAccountDetails,
    userId: string,
): boolean => {
    if (account.accountDetails.primaryUserId === userId) {
        return true;
    }

    if (account.type === MoneyMapAccountType.INTERNAL_BANKING) {
        const details = account.accountDetails as MoneyMapBankAccountDetails;
        return (
            details.jointUserId === userId &&
            details.jointUserRelationshipStatus === RelationshipStatus.ACTIVE
        );
    }

    if (account.type === MoneyMapAccountType.APEX_INVESTMENT_MANAGEMENT) {
        const details = account.accountDetails as MoneyMapApexAccountDetails;
        return (
            getApexRegistrationOwnershipLabel(details.apexAccountType) ===
            'Joint'
        );
    }

    if (account.type === MoneyMapAccountType.INVESTMENT_MANAGEMENT) {
        const details = account.accountDetails as MoneyMapIMAccountDetails;
        return (
            getLegacyImAccountOwnershipLabel(details.accountType) === 'Joint'
        );
    }

    // EXTERNAL_BANKING / EXTERNAL_HOLISTIC have no joint concept — primaryUserId is the whole story.
    return false;
};

const isExternalMoneyMapAccount = (
    account: MoneyMapAccountWithAccountDetails,
): boolean =>
    account.type === MoneyMapAccountType.EXTERNAL_BANKING ||
    account.type === MoneyMapAccountType.EXTERNAL_HOLISTIC;

enum AccountSortTier {
    BILLS,
    SPEND,
    GOAL_CASH,
    GOAL_IM,
    OTHER_FRUITFUL,
    EXTERNAL,
}

const getGoalAccountSortTier = (
    account: MoneyMapAccountWithAccountDetails,
): AccountSortTier =>
    account.type === MoneyMapAccountType.INTERNAL_BANKING
        ? AccountSortTier.GOAL_CASH
        : AccountSortTier.GOAL_IM;

/**
 * Sort key for an account in the account dropdown: [tier, primary, secondary].
 * Tiers, in order:
 *   1. bill accounts
 *   2. spend accounts
 *   3. goal cash accounts
 *   4. goal investment-management accounts
 *   5. other Fruitful accounts
 *   6. externally linked accounts
 * Within a tier, accounts follow money map order. Grouped rules (goal sets)
 * are ordered by their group's priority, then by their own priority within
 * the group, as in the money map itself. An account with several rules uses
 * its best-ranked one.
 */
const getMoneyMapAccountSortKey = (
    account: MoneyMapAccountWithAccountDetails,
    moneyMap: DeepPartial<MoneyMapWithRulesAndAccounts> | null | undefined,
): [number, number, number] => {
    if (isExternalMoneyMapAccount(account)) {
        return [AccountSortTier.EXTERNAL, 0, 0];
    }

    const ruleKeys = (moneyMap?.moneyMapRules ?? [])
        .filter((rule) => rule?.moneyMapAccountId === account.id)
        .map((rule): [number, number, number] => {
            const tier =
                rule?.type === MoneyMapRuleType.BILLS
                    ? AccountSortTier.BILLS
                    : rule?.type === MoneyMapRuleType.SPEND
                      ? AccountSortTier.SPEND
                      : getGoalAccountSortTier(account);
            const group =
                rule?.moneyMapRuleGroupId != null
                    ? moneyMap?.moneyMapRuleGroups?.find(
                          (g) => g?.id === rule.moneyMapRuleGroupId,
                      )
                    : undefined;
            return group != null
                ? [tier, group.priority ?? 0, rule?.priority ?? 0]
                : [tier, rule?.priority ?? 0, 0];
        });

    if (ruleKeys.length === 0) {
        return [AccountSortTier.OTHER_FRUITFUL, 0, 0];
    }
    return orderBy(ruleKeys, [0, 1, 2])[0] as [number, number, number];
};

/**
 * Orders accounts for the account dropdown (see getMoneyMapAccountSortKey).
 * Ties, including all other Fruitful and external accounts, are ordered
 * alphabetically by name.
 */
export const sortMoneyMapAccounts = (
    accounts: MoneyMapAccountWithAccountDetails[],
    moneyMap: DeepPartial<MoneyMapWithRulesAndAccounts> | null | undefined,
): MoneyMapAccountWithAccountDetails[] => {
    return orderBy(
        accounts,
        [
            (account) => getMoneyMapAccountSortKey(account, moneyMap)[0],
            (account) => getMoneyMapAccountSortKey(account, moneyMap)[1],
            (account) => getMoneyMapAccountSortKey(account, moneyMap)[2],
            (account) => account.accountDetails.name.toLowerCase(),
        ],
        ['asc', 'asc', 'asc', 'asc'],
    );
};
